import React, { useState, useEffect } from 'react';
import SetupStripe from './SetupStripe';
import CreatePlan from './CreatePlan';
import ImportSubscribers from './ImportSubscribers';
import PlanManagement from './PlanManagement';
import SubscriptionAnalytics from './SubscriptionAnalytics';
import StripeAccountInfo from './StripeAccountInfo';
import { useAuth } from '../../contexts/AuthContext';
import { facilityApi } from '../../api';
import { Facility } from '../../api_types';

const Payments: React.FC<{ facilityId: string, businessId: string }> = ({ facilityId, businessId }) => {
    const [activeTab, setActiveTab] = useState<'setup' | 'plans' | 'create' | 'import' | 'analytics' | 'account'>('setup');
    const { currentUser, businessData } = useAuth();
    const [facility, setFacility] = useState<Facility | null>(null);

    useEffect(() => {
        const fetchFacility = async () => {
            if (businessId && facilityId) {
                try {
                    const facilityData = await facilityApi.getFacility(facilityId, businessId);
                    setFacility(facilityData);
                } catch (err) {
                    setFacility(null);
                }
            }
        };
        fetchFacility();
    }, [businessId, facilityId]);

    // Log businessData on every render
    console.log('Payments render', { businessData });

    const renderContent = () => {
        switch (activeTab) {
            case 'setup':
                return <SetupStripe businessId={businessId} facilityId={facilityId} />;
            case 'account':
                return <StripeAccountInfo businessId={businessId} facilityId={facilityId} />;
            case 'create':
                return !facility?.isPPU ? <CreatePlan businessId={businessId} facilityId={facilityId} /> : null;
            case 'plans':
                return !facility?.isPPU ? <PlanManagement businessId={businessId} facilityId={facilityId} /> : null;
            case 'analytics':
                return facility?.id ? <SubscriptionAnalytics businessId={businessId} facilityId={facility.id} /> : null;
            case 'import':
                return !facility?.isPPU ? <ImportSubscribers businessId={businessId} facilityId={facilityId} /> : null;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Payments & Subscriptions</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Manage your Stripe Connect account, subscription plans, and member subscriptions
                    </p>
                </div>

                <div className="bg-white shadow rounded-lg">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex">
                            <button
                                onClick={() => setActiveTab('setup')}
                                className={`py-4 px-6 text-sm font-medium ${
                                    activeTab === 'setup'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Stripe Setup
                            </button>
                            <button
                                onClick={() => setActiveTab('account')}
                                className={`py-4 px-6 text-sm font-medium ${
                                    activeTab === 'account'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Account Info
                            </button>
                            {!facility?.isPPU && (
                                <>
                                    <button
                                        onClick={() => setActiveTab('create')}
                                        className={`py-4 px-6 text-sm font-medium ${
                                            activeTab === 'create'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        Create Plan
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('plans')}
                                        className={`py-4 px-6 text-sm font-medium ${
                                            activeTab === 'plans'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        Plans
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => setActiveTab('analytics')}
                                className={`py-4 px-6 text-sm font-medium ${
                                    activeTab === 'analytics'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Analytics
                            </button>
                            {!facility?.isPPU && (
                                <button
                                    onClick={() => setActiveTab('import')}
                                    className={`py-4 px-6 text-sm font-medium ${
                                        activeTab === 'import'
                                            ? 'border-b-2 border-blue-500 text-blue-600'
                                            : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Import Subscribers
                                </button>
                            )}
                        </nav>
                    </div>

                    <div className="p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Payments; 